/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CompanyDataPipeline.module.scss';

type Step = {
  id: string;
  num: string;
  title: string;
  description: string;
};

type Translate = (key: string) => string;

const getSteps = (t: Translate): Step[] => [
  {
    id: 'pipeline-step-1',
    num: '01',
    title: t('Entity resolution'),
    description: t(
      'Turn a name, domain, VAT number or registration number into one registry record. Similarly named companies are the first place an agent goes wrong, so the resolved registration number is returned with the match.',
    ),
  },
  {
    id: 'pipeline-step-2',
    num: '02',
    title: t('KYB verification'),
    description: t(
      'Legal name, legal form, registration number, incorporation date, registered office, current status and the officers on file. KYB checks read from the statutory register, not inferred from a website.',
    ),
  },
  {
    id: 'pipeline-step-3',
    num: '03',
    title: t('UBO and ownership verification'),
    description: t(
      'Shareholders, beneficial ownership data, parents and subsidiaries, hop by hop across jurisdictions. Each hop is a registry lookup and each holding percentage is returned with the filing that recorded it.',
    ),
  },
  {
    id: 'pipeline-step-4',
    num: '04',
    title: t('Read financials'),
    description: t(
      'Filed financial statements as structured fields: turnover, profit, assets, liabilities, employees, by period. Where a register publishes documents rather than data, the fields are extracted from the filed document and the document reference stays attached.',
    ),
  },
  {
    id: 'pipeline-step-5',
    num: '05',
    title: t('Cite'),
    description: t(
      'Every value in the response carries the register, the filing reference, the retrieval timestamp and a link to the original document where the register publishes one. The agent passes these through to the user or the case file.',
    ),
  },
  {
    id: 'pipeline-step-6',
    num: '06',
    title: t('Log'),
    description: t(
      'The response is structured, so the resolved entity, the fields used and the sources can be written to the decision log without parsing prose. Re-running the query reproduces the same values against the same timestamp.',
    ),
  },
];

export const CompanyDataPipeline = () => {
  const { t } = useInt();
  const [activeIndex, setActiveIndex] = useState(0);
  const STEPS = getSteps(t);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const elements = stepRefs.current.filter((el): el is HTMLDivElement => el !== null);

    if (elements.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = elements.indexOf(entry.target as HTMLDivElement);

            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        });
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleIndexClick = (index: number) => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    stepRefs.current[index]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <Section className={styles.root}>
      <div className={styles.glowBlue} aria-hidden="true" />
      <div className={styles.glowIndigo} aria-hidden="true" />

      <div className={styles.grid}>
        <aside className={styles.side}>
          <TitleBlock
            align="left"
            title={t('Six things an agent does with a company before it acts.')}
            description={t(
              'Onboarding a supplier, underwriting a borrower or screening a counterparty runs the same sequence. Each step is a dataset and a call, and each call returns the record with its source attached.',
            )}
          />

          <ol className={styles.index}>
            {STEPS.map((step, i) => (
              <li key={step.id}>
                <button
                  type="button"
                  className={cn(styles.indexItem, i === activeIndex && styles.indexItemActive)}
                  onClick={() => handleIndexClick(i)}
                >
                  <span className={styles.indexNum}>{step.num}</span>
                  {step.title}
                </button>
              </li>
            ))}
          </ol>
        </aside>

        <div className={styles.steps}>
          {STEPS.map((step, i) => (
            <div key={step.id} className={styles.stepRow}>
              <div className={styles.rail}>
                <span className={cn(styles.stepNum, i === activeIndex && styles.stepNumActive)}>{step.num}</span>
                {i < STEPS.length - 1 && <span className={styles.connector} />}
              </div>

              <div
                ref={(el) => {
                  stepRefs.current[i] = el;
                }}
                className={cn(styles.stepCard, i === activeIndex && styles.stepCardActive)}
              >
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDesc}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
